import { useEffect } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { EBuildingAssignments } from 'enums';
import { IBuilding } from 'models';
import { useBuildings } from 'pages/buildings/buildings.hook';
import { useAssignBuildingsToUserMutation, useFetchUserAssignedBuildingsQuery } from 'store/apis';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';

type AssignedBuilding = {
  buildingId: number;
  assignment: string;
  address: string;
};

interface UseUserAssignedBuildingsFormManagerProps {
  userId: number;
  next?: (data?: any) => void;
}

export const useUserAssignedBuildingsFormManager = ({
  userId,
  next = () => {},
}: UseUserAssignedBuildingsFormManagerProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [loadingAllBuildings, allBuildings] = useBuildings();
  const { data: userAssignedbuildings = [], isLoading: loadingUserAssignedBuildings } =
    useFetchUserAssignedBuildingsQuery({
      orgSlug,
      userId,
    });
  const [
    assignBuildings,
    { isLoading: loading_assignBuildings, isSuccess: succes_assignBuildings, isError: error_assignBuildings },
  ] = useAssignBuildingsToUserMutation();

  const assignedBuildings = userAssignedbuildings.map(
    ({ address = 'No address', user_assignment: { building_id: buildingId, assignment } }: any) => ({
      buildingId,
      address,
      assignment,
    }),
  ) as AssignedBuilding[];

  const unassignedBuildings = allBuildings.filter(
    ({ id }) => !assignedBuildings.map(({ buildingId }) => buildingId).includes(id),
  );

  const initialValues = {
    assignment: EBuildingAssignments.CLEANER,
    buildingIds: [] as number[],
  };

  const validationSchema = Yup.object({
    assignment: Yup.string().required(),
    buildingIds: Yup.array().min(1, 'Select at least one building'),
  });

  const formManager = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: ({ assignment, buildingIds }) => {
      assignBuildings({
        orgSlug,
        userId,
        data: {
          assignment,
          buildingIds,
        },
      });
    },
  });

  useEffect(() => {
    if (!loading_assignBuildings && succes_assignBuildings && !error_assignBuildings) {
      formManager.setErrors({});
      formManager.setTouched({});
      formManager.setFieldValue('buildingIds', []);

      next();
    }
  }, [loading_assignBuildings, succes_assignBuildings, error_assignBuildings]);

  return [
    {
      assignedBuildings,
      unassignedBuildings,
      formManager,
      loading_form: loadingAllBuildings || loadingUserAssignedBuildings,
      loading_submit: loading_assignBuildings,
    },
  ] as [
    {
      assignedBuildings: AssignedBuilding[];
      unassignedBuildings: IBuilding[];
      formManager: FormikProps<typeof initialValues>;
      loading_form: boolean;
      loading_submit: boolean;
    },
  ];
};
